import React from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, CheckCircle, Warning} from '../../icons';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {platformName} from '../../../utils/filterNotes';
import {dailyLimitOf, formatLimitAmount, hasAccess, nextPageCount, reconnectKindOf} from './accountsModel';
import {isPaidReconnectChannel, ownerShort} from '../connections/reconnectAccountText';

/** El logo del canal en una caja fija: los logos traen márgenes propios por canal y desalineaban. */
export function AccountLogo({channel, size = 26}) {
    return (
        // El nombre de la plataforma, para el lector de pantalla (sin `title` nativo: review C-11).
        <span className="aac-logo" style={{width: size, height: size}} role="img" aria-label={platformName(channel)}>
            <ChannelLogo channel={channel} width={size - 4} height={size - 4} hspace={0} vspace={0}
                         marginLeft={0} padding={0} useOld={false}/>
        </span>
    );
}

/**
 * Logo, nombre e ID. Con `onOpen` el nombre es un botón que abre el detalle (Conectadas); sin él,
 * texto (Sin cliente, Ignoradas: no hay detalle de una cuenta que no es de ningún cliente).
 */
export function AccountIdentity({row, onOpen}) {
    const name = row.name || row.accountId;
    return (
        <span className="aac-acct">
            <AccountLogo channel={row.channel}/>
            <span className="aac-acct__tx">
                {onOpen
                    ? <button type="button" className="aac-acct__name -link" onClick={onOpen}>{name}</button>
                    : <span className="aac-acct__name">{name}</span>}
                {row.name && row.accountId && <span className="aac-acct__id">{row.accountId}</span>}
            </span>
        </span>
    );
}

export function LimitCell({row}) {
    const {t, i18n} = useTranslation();
    const limit = dailyLimitOf(row);
    if (limit.state === 'na') {
        return <span className="aac-muted">{t('agency_v2.accounts.limit.na')}</span>;
    }
    if (limit.state === 'none') {
        return <span className="aac-pill -none">{t('agency_v2.accounts.limit.none')}</span>;
    }
    return (
        <span className="aac-pill">
            {t('agency_v2.accounts.limit.value', {
                amount: formatLimitAmount(limit.currency, limit.value, i18n.language),
                interpolation: {escapeValue: false},
            })}
        </span>
    );
}

/** El verbo que recupera una cuenta caída: «Arreglar conexión» en una planilla, «Reconectar» si no. */
export function reconnectLabel(t, row) {
    return reconnectKindOf(row) === 'sheet' ? t('clients.fix_sheet_connection') : t('agency_v2.accounts.reconnect');
}

/**
 * «Al día» o «Sin acceso». Con acceso caído y un gesto que la recupere, el botón va al lado: es lo
 * que la fila tiene para hacer, y antes vivía detrás de un tooltip. Debajo, en las cuentas de pauta,
 * quién la ve («La ve Cami»): es con quién hay que entrar para reconectarla (UD-20, M4).
 */
export function AccessCell({row, onReconnect}) {
    const {t} = useTranslation();
    if (hasAccess(row)) {
        return (
            <span className="aac-access -ok">
                <CheckCircle size={16} weight="bold" aria-hidden="true"/>
                {t('agency_v2.accounts.access.ok')}
            </span>
        );
    }
    const canReconnect = !!reconnectKindOf(row) && !!onReconnect;
    const owner = isPaidReconnectChannel(row.channel) ? ownerShort(t, row.raw) : null;
    return (
        <span className="aac-access-cell">
            <span className="aac-access -err">
                <Warning size={16} weight="bold" aria-hidden="true"/>
                {t('agency_v2.accounts.access.error')}
            </span>
            {canReconnect && (
                <button type="button" className="aac-btn -sm" onClick={() => onReconnect(row)}>
                    <ArrowsClockwise size={13} weight="bold" aria-hidden="true"/>
                    {reconnectLabel(t, row)}
                </button>
            )}
            {owner && <span className="aac-access-owner" data-testid="aac-access-owner">{owner}</span>}
        </span>
    );
}

/** «Mostrando N de M» y «Mostrar 50 más». Se dibuja siempre que haya filas: el total también informa. */
export function ListFooter({shown, total, onMore}) {
    const {t} = useTranslation();
    const more = nextPageCount(shown, total);
    return (
        <div className="aac-foot">
            <span>{t('agency_v2.accounts.showing', {shown, total})}</span>
            {more > 0 && (
                <button type="button" className="aac-btn -sm" onClick={onMore}>
                    {t('agency_v2.accounts.show_more', {count: more})}
                </button>
            )}
        </div>
    );
}
